import {
  ApiOutlined,
  CloseOutlined,
  GlobalOutlined,
  LinkOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';

import {
  Button,
  Checkbox,
  Heading,
  RadioInput,
  Tooltip,
  Option,
} from '@mm/components';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import {
  CampaignType,
  CampaignAudienceAttributeExclusionType,
  CampaignAudienceAttributeExclusionKeys,
  CampaignAudienceExclusions,
} from '@mm/state/model/CampaignsModel';
import { ReactComponent as AudienceIcon } from 'assets/audiences-icon-left-menu.svg';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';

import 'prosemirror-view/style/prosemirror.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationEmailSmsSettingsSlideout.module.css';
import { ReactComponent as SendGridIcon } from 'assets/sendgrid.svg';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { ConfigProvider, InputNumber, Switch } from 'antd';
import { AudiencesSelector } from '../../../../features/campaigns/components/AudiencesSelector/AudiencesSelector';
import { IntegrationsSelector } from '../../../../features/beta/modules/Integrations/IntegrationsSelector/IntegrationsSelector';
import { selectAudiences } from '@mm/state/selectors/messengerAdSelectors';

export type IAutomationEmailSmsSettingsSlideout = {
  className?: string;
  campaignId?: number;
};

const mapAccountToOpt = ({ id, emailAddress, kind }) => ({
  id,
  name: emailAddress,
  isSendGrid: kind === 'sendgrid',
});

export const AutomationEmailSmsSettingsSlideout: React.FC<
  IAutomationEmailSmsSettingsSlideout
> = props => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const { campaignId } = props;

  const campaign = useSelector<
    StoreState,
    CampaignTypeWithMappedPurposeAndDisplayStatus | undefined
  >(
    state => selectCampaignById(state, campaignId || -1),
    (l, r) =>
      JSON.stringify({
        a: l?.emailAccountsIds,
        b: l?.suppressionListEnabled,
      }) ===
      JSON.stringify({ a: r?.emailAccountsIds, b: r?.suppressionListEnabled }),
  );

  const defaultEmailAccountsIds = campaign?.emailAccountsIds || [];
  const defaultSuppressionListEnabled = !!campaign?.suppressionListEnabled;

  const isDefaultRobin = defaultEmailAccountsIds.length > 1;

  const [opt, setOpt] = useState(isDefaultRobin ? 'round_robin' : 'single');

  const accounts = useSelector(selectActiveEmailAccounts);

  const defaultOptions = accounts.map(mapAccountToOpt);

  const defaultEmailSelected =
    defaultEmailAccountsIds.length === 1
      ? defaultEmailAccountsIds[0]
      : undefined;

  const defaultCheckboxes = isDefaultRobin
    ? defaultEmailAccountsIds.reduce(
        (a, v) => ({
          ...a,
          [v]: true,
        }),
        {},
      )
    : {};

  const exclusionOptions = [
    {
      key: 'visitLastDays',
      icon: <GlobalOutlined className={css.icon} />,
      title: 'Visited website in last',
      input: true,
    },
    {
      key: 'emailReceivedLastDays',
      icon: <MailOutlined className={css.icon} />,
      title: 'Received email in last',
      input: true,
    },
    {
      key: 'emailClickedLastDays',
      icon: <LinkOutlined className={css.icon} />,
      title: 'Clicked email in last',
      input: true,
    },
    {
      key: 'purchaseLastDays',
      icon: <ShoppingCartOutlined className={css.icon} />,
      title: 'Purchased product in last',
      input: true,
    },
    {
      key: 'audienceIds',
      icon: <AudienceIcon style={{ color: ' #262626' }} className={css.icon} />,
      title: 'Audiences',
    },
    {
      key: 'crmSynced',
      icon: <ApiOutlined className={css.icon} />,
      title: 'Synced to CRM',
    },
  ];

  const getDefaultExclusions = () => {
    const excludedAttr = campaign?.campaignAudiencesAttributes?.find(
      attr => attr.exclusionsSettings,
    );

    if (excludedAttr) {
      const hasExclusionKey = Object.keys(excludedAttr).some(key =>
        CampaignAudienceAttributeExclusionKeys.includes(
          key as CampaignAudienceAttributeExclusionType,
        ),
      );

      return hasExclusionKey ? excludedAttr : {};
    }

    return {};
  };

  const defaultExclusions = getDefaultExclusions();
  const [exclusions, setExclusions] =
    useState<CampaignAudienceExclusions>(defaultExclusions);
  const [emailOptions, setEmailOptions] = useState(defaultOptions);
  const [checkboxes, setCheckboxes] =
    useState<Record<number, boolean>>(defaultCheckboxes);

  const onCheckboxClick = id => {
    setCheckboxes({ ...checkboxes, [id]: !checkboxes[id] });
  };

  const [emailSelectedId, setEmailSelectedId] = useState<number | undefined>(
    defaultEmailSelected,
  );

  const onEmailOptionsChange = opts => {
    setEmailSelectedId(opts.id);
  };

  const [isEmailSuppressionListEnabled, setIsEmailSuppressionListEnabled] =
    useState(defaultSuppressionListEnabled);

  const history = useHistory();

  const modalActions = useModalActions();

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;
  const hasExclusions = Object.keys(exclusions).some(Boolean);

  const getPayload = () => {
    const payload = {
      id: campaignId,
      emailAccountsIds: [],
      suppressionListEnabled: isEmailSuppressionListEnabled,
      ...(hasExclusions && {
        campaignAudiencesAttributes: [{ kind: 'excluded', ...exclusions }],
      }),
    } as unknown as CampaignType;

    switch (opt) {
      case 'single': {
        payload.emailAccountsIds = emailSelectedId ? [emailSelectedId] : [];
        break;
      }
      case 'round_robin': {
        payload.emailAccountsIds = Object.keys(checkboxes)
          .filter(k => checkboxes[k])
          .map(v => parseInt(v, 10));
        break;
      }
      default:
        break;
    }

    return payload;
  };

  const payload = useMemo(
    () => getPayload(),
    [opt, checkboxes, emailSelectedId],
  );

  const hasNoEmailChanges =
    payload.emailAccountsIds?.sort().join('') ===
    defaultEmailAccountsIds.sort().join('');

  const hasNoContactListChanges =
    !!payload.suppressionListEnabled === defaultSuppressionListEnabled;

  const hasNoChanges = hasNoEmailChanges && hasNoContactListChanges;

  const onSave = async () => {
    setBusy(true);
    try {
      const payload = getPayload();

      const res = await campaignCRUDActions.updateCampaign(payload, chatbotId);

      const relatedCampaignId = res?.campaign?.relatedCampaigns?.[0]?.id;

      if (relatedCampaignId) {
        await campaignCRUDActions.retrieve(relatedCampaignId);
      }

      successToast('Your changes have been saved.', 'top-left');

      closeSlideout();
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const onBeforeClose = () => {
    if (hasNoChanges) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      modalActions.openGeneralConfirmModal({
        headerText: 'You have unsaved changes',
        messageText: 'Are you sure that you want to leave without saving?',
        onCloseHandler: ok => {
          if (ok) {
            resolve(true);
          } else {
            reject();
          }
        },
        okButtonText: "Yes I'm sure",
        cancelButtonText: 'Cancel',
      });
    });
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(onBeforeClose);

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const emailAccountsCRUDActions = useEmailAccountCRUDActions();

  const audiences = useSelector(selectAudiences);

  const selectedAudiencesOptions = (
    Array.isArray(exclusions?.audienceIds) ? exclusions.audienceIds : []
  )
    .map(id => {
      const audience = audiences.find(a => a.id === id);
      return audience ? { id, label: audience.name } : null;
    })
    .filter(Boolean) as Option[];

  const onChange = (
    key: string,
    value: number | string[] | number[] | Option[],
    isActive?: boolean,
  ) => {
    setExclusions((prevExclusions: Record<string, any>) => {
      if (!isActive) {
        const { [key]: _, ...rest } = prevExclusions;
        return rest;
      }
      return {
        ...prevExclusions,
        [key]: value,
      };
    });
  };

  const [inputValues, setInputValues] = useState(
    exclusionOptions.reduce((acc, item) => {
      if (item.key === 'audienceIds' || item.key === 'crmSynced') {
        acc[item.key] = defaultExclusions[item.key] || [];
      } else {
        acc[item.key] = defaultExclusions[item.key] || 10;
      }

      return acc;
    }, {}),
  );

  const handleInputChange = (key: string, value: number) => {
    setInputValues(prev => ({ ...prev, [key]: value }));
    onChange(key, value, true);
  };

  const inputDisabled = (key: string) => !exclusions[key] || busy;

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const res = await emailAccountsCRUDActions.getAccounts(activeFbPageId);

        if (Array.isArray(res?.emailAccounts)) {
          const mapped = res.emailAccounts.map(mapAccountToOpt);
          setEmailOptions(mapped);

          if (!defaultEmailAccountsIds.length) {
            setEmailSelectedId(res.emailAccounts[0]?.id);
          }
        }
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const configureEmailSendersContent = (
    <>
      <RadioInput
        disabled={isSaveDisabled}
        className={css.radio}
        checked={opt === 'single'}
        name="Use a single email"
        label="Use a single email"
        value="single"
        onClick={() => setOpt('single')}
      />

      {opt === 'single' && (
        <PrimarySelect
          disabled={isSaveDisabled}
          className={css.select}
          options={emailOptions}
          value={emailOptions.find(
            v =>
              emailSelectedId && v.id.toString() === emailSelectedId.toString(),
          )}
          onChange={onEmailOptionsChange}
        />
      )}

      <div className={css.robin}>
        <RadioInput
          disabled={isSaveDisabled}
          className={css.radio}
          checked={opt === 'round_robin'}
          name="Multiple Emails (Round Robin)"
          label="Multiple Emails (Round Robin)"
          value="round_robin"
          onClick={() => setOpt('round_robin')}
        />
        <Tooltip
          title={
            <span className={css.popover}>
              Emails will be rotated through to start new Conversations with
              Leads. Leads with existing Email Conversations will continue to
              receive emails from the same Email Address.
            </span>
          }
        >
          <span className={css.info}>
            <QuestionCircleOutlined />
          </span>
        </Tooltip>
      </div>

      {opt === 'round_robin' && (
        <>
          <div className={css.checkboxes}>
            {emailOptions.map(v => (
              <Checkbox
                disabled={isSaveDisabled}
                isChecked={checkboxes[v.id]}
                onClick={() => onCheckboxClick(v.id)}
                label={
                  <div className={css.checkboxLabel}>
                    {v.name}
                    {v.isSendGrid && <SendGridIcon style={{ width: '14px' }} />}
                  </div>
                }
                key={v.id}
              />
            ))}
          </div>
          <Button
            css={{ container: css.connect }}
            color="secondary-blue"
            onClick={() => {
              closeSlideout();
              modalActions.openActivateEmailAccountModal();
            }}
          >
            Connect new email
          </Button>
        </>
      )}
    </>
  );

  const canRebalance = Object.values(defaultCheckboxes).some(Boolean);

  const rebalanceEmailQueueContent = canRebalance ? (
    <section className={css.rebalanceSection}>
      <div className={css.heading}>Email queue</div>
      <div>
        <Button
          css={{ container: css.connect }}
          color="secondary-blue"
          disabled={!campaignId || !canRebalance}
          onClick={async () => {
            if (campaignId) {
              try {
                await campaignCRUDActions.rebalanceRoundRobin(campaignId);
                successToast('Rebalance queue is in progress');
              } catch (err) {
                errorToast("Couldn't start queue rebalance");
              }
            }
          }}
        >
          Rebalance queue
        </Button>
      </div>
    </section>
  ) : (
    <></>
  );

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <Heading size={3}>Settings</Heading>
        <Button
          css={{ container: css.save }}
          onClick={onSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <section className={css.section}>
          <div className={css.heading}>Email </div>
          {configureEmailSendersContent}
          {rebalanceEmailQueueContent}
        </section>

        <section className={css.section}>
          <div className={css.heading}>Suppression</div>
          <div className={css.contactList}>
            <ConfigProvider
              theme={{
                components: {
                  Switch: {
                    colorPrimary: 'var(--color-geekblue-6)',
                  },
                },
              }}
            >
              <Switch
                size="small"
                checked={isEmailSuppressionListEnabled}
                disabled={busy}
                onChange={() =>
                  setIsEmailSuppressionListEnabled(
                    !isEmailSuppressionListEnabled,
                  )
                }
                className={css.contactListSwitch}
              />
            </ConfigProvider>

            <Heading isBold={false} size={5}>
              Use Email Suppression List
            </Heading>

            <Button
              css={{ container: css.contactListButton }}
              color="secondary-blue"
              onClick={() => {
                history.push(
                  BotBuilderRoutes.integrations.getLink({ chatbotId }),
                  {
                    kind: 'contact-suppression',
                  },
                );
                closeSlideout();
                // modalActions.openEmailSuppressionListModal()
              }}
            >
              Suppression Settings
            </Button>
          </div>
        </section>

        <section className={css.section}>
          <div className={css.heading}>Exclusion</div>
          {exclusionOptions.map(item => (
            <div className={css.exclusionRow} key={item.key}>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: 'var(--color-geekblue-6)',
                    },
                  },
                }}
              >
                <Switch
                  size="small"
                  checked={exclusions[item.key]}
                  disabled={busy}
                  onChange={(active: boolean) => {
                    onChange(item.key, inputValues[item.key], active);
                  }}
                  className={css.contactListSwitch}
                />
              </ConfigProvider>
              {item.icon}
              <span>{item.title}</span>
              {item.input && (
                <>
                  <InputNumber
                    min={1}
                    value={inputValues[item.key]}
                    onChange={v => handleInputChange(item.key, v)}
                    status={!inputValues[item.key] ? 'error' : ''}
                    disabled={inputDisabled(item.key)}
                  />{' '}
                  <span>days</span>
                </>
              )}
              {item.key === 'audienceIds' && (
                <AudiencesSelector
                  newSelect
                  placeholder="Choose"
                  disabled={inputDisabled(item.key)}
                  className={css.audiencesSelector}
                  selected={selectedAudiencesOptions || []}
                  onChange={v => {
                    onChange(item.key, v.map(a => a.id) as number[], true);
                  }}
                />
              )}
              {item.key === 'crmSynced' && (
                <IntegrationsSelector
                  availableOptions={[
                    'klaviyo',
                    'hubspot',
                    'sendlane',
                    'salesforce',
                    'slack',
                    'shopify',
                    'mailchimp',
                    'json_webhook',
                    'gohighlevel',
                    'omnisend',
                  ]}
                  disabled={inputDisabled(item.key)}
                  selectedIntegrations={
                    (exclusions[item.key] as string[]) || []
                  }
                  onChange={v => {
                    onChange(item.key, v, true);
                  }}
                />
              )}
            </div>
          ))}
        </section>
      </Menu.Content>
    </Menu>
  );
};
